import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MaxWidth, ResponsiveService } from '../shared/responsive/responsive.service';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { STATE_HOME_PAGE } from '../shared/app-states';
import { GaMessageService } from '../shared/message/ga-message.service';
import { GaStateService } from '../shared/services/ga-state.service';
import { urlUtil, util } from '../shared/util';
import { EVENT_OPEN_BROWSELIST_EDIT_DIALOG, GA1Service } from '../shared/ga1/ga1.service';
import { EVENT_UPDATE_MAIN_URL } from '../shared/app-events';
import { BrowseListService } from '../shared/browselists/browselists.service';
import { DOCUMENT } from '@angular/common';
import { GaStateDeclaration, IExtendedDocument } from '@type_definitions/datatypes';
import { GaEventService, IGAEvent } from '../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { TitleService } from '../shared/services/title.service';
import _ from 'underscore';

export const MAIN_CONTAINER_ELEMENT:string = 'ga2-content';

const DEFAULT_MAIN_URL:string = '/unacc/AM_HomeView_Read.jsp';
const GA1_FRAME_TOP_URL:string = '/unacc/Top.jsp';
const GA1_FRAME_MENU_TREE_URL:string = '/unacc/MenuTree.jsp?keyfield=demence&keyvalue=GA';
const GA1_IFRAME_NAME:string = 'iframe#content';

@Component({
  selector: 'ga2-main',
  template: `
    <main-layout [hideTop]="hideTop" [hideMenu]="hideMenu" [noMenu]="noMenu" [frameless]="frameless">
      <div class="ui-layout-north" style="display:none">
        <iframe class="border-0" src="${GA1_FRAME_TOP_URL}" id="topFrame" name="topFrame" [scrolling]="'no'" width="100%" height="100%"></iframe>
      </div>
      <div class="ui-layout-west" style="display:none">
        <iframe class="border-0" id="menu" src="${GA1_FRAME_MENU_TREE_URL}" name="menu" width="100%" height="100%"></iframe>
      </div>
      <div class="ui-layout-center" style="display:none">
        <h2 *ngIf="ga1frame && contentLoading" class="ga1-activity-indicator">
          <activity-indicator [useDefaultText]="true"></activity-indicator>
        </h2>

        <iframe [hidden]="!ga1frame" class="border-0" [ngStyle]="{'visibility': contentLoading? 'hidden': 'visible' }" id="content" name="content" width="100%" height="100%" (load)="onContentFrameLoad()"></iframe>
        <div *ngIf="!ga1frame" [id]="mainContentElement" scrollTarget="content-top">
          <div class="container main" [maxWidth]="maxWidth">
            <div ui-view="content"></div>
            <!-- viewport for ga3 content -->
            <div ui-view=""></div>
          </div>
        </div>
      </div>
      <iframe class="feedback-frame d-none border-0" src="/unacc/FeedbackFrame.jsp" name="feedbackFrame" width="100%" height="100%"></iframe>
      <!-- needed for MainParamsCtrl -->
      <span ui-view="params"></span>
    </main-layout>
  `
})
export class MainComponent implements OnInit, OnDestroy {
  @Input() public hideTop:boolean;
  @Input() public hideMenu:boolean;
  @Input() public noMenu:boolean;
  @Input() public ga1frame:boolean;
  @Input() public frameless:boolean;

  public mainContentElement:string = MAIN_CONTAINER_ELEMENT;
  public contentLoading:boolean;
  public maxWidth:MaxWidth;

  private ga1ContentUrl:string = '';
  private fromState:GaStateDeclaration;
  private onStateSuccessHook:Function;
  private ga1EventsSubscription:Subscription;
  private eventsSubscription:Subscription;
  
  constructor(
    private stateService:StateService,
    private messageService:GaMessageService,
    private gaStateService:GaStateService,
    private responsiveService:ResponsiveService,
    private ga1Service:GA1Service,
    private transitionService:TransitionService,
    private browseListService:BrowseListService,
    private eventService:GaEventService,
    private changeDetectorRef:ChangeDetectorRef,
    private titleService:TitleService,
    @Inject(DOCUMENT) private document:IExtendedDocument
  ) {
    /**/
  }
  
  public ngOnInit():void {
    this.maxWidth = MaxWidth.SM;
    if (this.stateService.params.errormessage) {
      this.messageService.showErrorMessage(this.stateService.params.errormessage, true);
      //remove the errormessage param from the url
      this.gaStateService.updateStateParameter('errormessage', null);
    }

    if (util.notNullOrUndefined(this.responsiveService.width)) {
      this.maxWidth = this.responsiveService.width;
    }

    this.setEditMode();
    this.listen();
    this.changeDetectorRef.detectChanges();
  }

  private listen():void {
    this.onStateSuccessHook = this.transitionService.onSuccess({}, (transition:Transition) => {
      this.ga1frame = transition.to()?.name === STATE_HOME_PAGE;
      this.fromState = transition.from();
      this.changeDetectorRef.detectChanges();
    });

    this.eventsSubscription = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_UPDATE_MAIN_URL) {
        this.setContentFrameUrl(event.data);
      }
    });

    this.ga1EventsSubscription = this.ga1Service.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_OPEN_BROWSELIST_EDIT_DIALOG) {
        this.browseListService.openBrowselistEditDialog(event.data);
      }
    });
  }

  /**
   * Handles the logic to be executed when the content frame (iframe) has finished loading.
   * This includes updating the application state, setting the document title,
   * updating the browser URL, and triggering change detection.
   *
   * @return {void} This method does not return any value.
   */
  public onContentFrameLoad():void {
    if (this.ga1frame) {
      // set content loading false
      this.contentLoading = false;

      // update window title based on iframe title
      this.titleService.setTitle(this.currentContentFrameTitle);

      // update browser window url
      this.setBrowserWindowUrl();

      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Updates the browser window URL based on the current state URL or a default value.
   * Checks for URL mismatches and modifies the application state accordingly.
   *
   * @return {void} No value is returned.
   */
  private setBrowserWindowUrl():void {
    // Compare the current state URL with the content frame URL and synchronize if they are different
    const mainUrl:string = this.stateService.params.url || DEFAULT_MAIN_URL;
    if (!this.contentLoading && this.checkContentFrameUrlChange(this.contentFrameUrl, mainUrl)) {
      console.debug('url mismatch, change state to url: ' + this.contentFrameUrl);
      this.ga1ContentUrl = this.contentFrameUrl;
      // update URL without reloading
      this.stateService.go(STATE_HOME_PAGE, { url: this.ga1ContentUrl });
    }
  }

  /**
   * Sets the content frame URL for the GA1 iframe and manages its loading behavior.
   *
   * This method ensures that the iframe URL is updated if the new URL is valid and different from the current URL.
   * It handles iframe loading state, validates the URL, and prevents unnecessary history entries in the browser.
   * If a reload is required depending on navigation states, the iframe is reloaded to reflect changes.
   *
   * @param {string} mainUrl The new URL to be loaded in the GA1 iframe. If no URL is provided, the default URL is used.
   * @return {void} Does not return a value.
   */
  private setContentFrameUrl(mainUrl:string):void {
    const newMainUrl:string = mainUrl || DEFAULT_MAIN_URL;

    /**
     * Updates and loads a new URL into the GA1 iframe if the URL is valid and different from the current one.
     *
     * - Ensures `newMainUrl` is not 'about:blank' and not equal to the current `ga1ContentUrl`.
     * - Sets `contentLoading` to true to indicate ongoing content loading.
     * - Updates the iframe's `src` using `location.replace()` to prevent adding a new history entry.
     */
    if (!_.isEqual(newMainUrl, this.ga1ContentUrl)) {
      this.contentLoading = true;
      console.debug('Load GA1 url ' + newMainUrl);
      this.ga1ContentUrl = newMainUrl;
      (<HTMLIFrameElement>this.jqDocument.find(GA1_IFRAME_NAME)[ 0 ]).contentWindow.location.replace(newMainUrl);
    }

    // when coming from a ga2/ga3 screen -> perform a content reload of ga1 iframe to reflect changes
    // do not reload when going to homepage (url empty)
    else if(this.fromState?.name !== STATE_HOME_PAGE && this.stateService.params.url) {
      this.contentLoading = true;
      (<HTMLIFrameElement>this.jqDocument.find(GA1_IFRAME_NAME)[ 0 ]).contentWindow.location.reload();
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Checks if the URL of the content frame has changed compared to the main URL.
   *
   * @param {string} contentFrameUrl - The URL of the content frame to be checked.
   * @param {string} mainUrl - The main URL to compare with the content frame URL.
   * @return {boolean} Returns true if the content frame URL has changed, false otherwise.
   */
  private checkContentFrameUrlChange(contentFrameUrl:string, mainUrl:string):boolean {
    return contentFrameUrl
      && contentFrameUrl !== 'about:blank'
      // for default url, we do not care about the reset of the params
      // for other urls, we do
      && (mainUrl.indexOf(DEFAULT_MAIN_URL) >= 0? contentFrameUrl.indexOf(DEFAULT_MAIN_URL) !== mainUrl.indexOf(DEFAULT_MAIN_URL) : contentFrameUrl !== mainUrl);
  }

  /**
   * Updates the editMode property of the ga1Service based on the stateService parameters.
   * The method sets the editMode to true if the 'editMode' parameter in stateService is equal to the string 'true',
   * otherwise sets it to false.
   *
   * @return {void} No value is returned by this method.
   */
  private setEditMode():void {
    this.ga1Service.editMode = this.stateService.params?.editMode === 'true';
  }

  /**
   * Retrieves the title of the content frame identified by the `GA1_IFRAME_NAME`.
   * Attempts to access the title property of the iframe's content document.
   * Handles potential cross-origin issues by ignoring the error and returning an undefined title.
   *
   * @return {string} The title of the content frame, or undefined if it is inaccessible.
   */
  private get currentContentFrameTitle():string {
    let contentFrameTitle:string;

    const contentFrame:JQuery<HTMLElement> = this.jqDocument.find(GA1_IFRAME_NAME);
    if (contentFrame.length) {
      try {
        contentFrameTitle = (<any>contentFrame[ 0 ]).contentDocument.title;
      } catch (e) {
        //probably a cross-origin issue, ignore
      }
    }

    return contentFrameTitle;
  }

  /**
   * Retrieves the URL of the content frame within the current document.
   * If the frame is inaccessible due to cross-origin restrictions, an empty or adjusted URL is returned.
   *
   * @return {string} The URL of the content frame without the origin, or an empty string if unavailable.
   */
  private get contentFrameUrl():string {
    let contentFrameUrl:string;

    const contentFrame:JQuery<HTMLElement> = this.jqDocument.find(GA1_IFRAME_NAME);
    if (contentFrame.length) {
      try {
        contentFrameUrl = (<HTMLIFrameElement>contentFrame[ 0 ]).contentWindow.location.href;
      } catch (e) {
        //probably a cross-origin issue, ignore
      }
    }

    return urlUtil.withoutOrigin(contentFrameUrl);
  }

  /**
   * Retrieves a jQuery-wrapped version of the current document object.
   *
   * @return {JQuery<HTMLElement>} A jQuery object representing the document.
   */
  private get jqDocument():JQuery<HTMLElement> {
    return $(this.document);
  }

  public ngOnDestroy():void {
    this.onStateSuccessHook();
    this.eventsSubscription?.unsubscribe();
    this.ga1EventsSubscription?.unsubscribe();
  }
}